import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { emojiToName, nameToEmoji } from 'gemoji';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { PillField } from '../field';
import { fieldErrorId, useElementId, useFieldChecks } from '../fieldIds';
import { EmojiPickerPopover } from '../internals/EmojiPickerPopover';
import { ReactionPills } from '../internals/ReactionPills';
import { PILL_CLASS, PILL_ERROR_CLASS, sizingClass } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

const ACTIVE_CLASS =
  'border-kind-act bg-kind-act text-background hover:bg-kind-act';

// Slack draws its own reaction pill as the whole primary: pressing it reacts.
// While `editable`, the emoji itself opens a picker to change what gets sent.
// A list `emoji` is a row of chosen emojis instead, and the card's primary reacts
export const Reaction: ComponentFn<typeof catalog, 'Reaction'> = ({
  props,
  bindings,
  emit,
}) => {
  const [emoji, setEmoji] = useBoundProp<string | string[]>(
    props.emoji,
    bindings?.emoji
  );
  const id = useElementId(props);
  const checks = useFieldChecks({
    path: bindings?.emoji,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
    value: typeof emoji === 'string' ? emoji : undefined,
  });
  const error = checks.error ?? props.error;
  const shortcode = props.emojiFormat === 'shortcode';
  const glyphOf = (value: string, served: string | undefined) =>
    shortcode ? (nameToEmoji[value] ?? served ?? value) : value;
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const [pickerOpen, setPickerOpen] = useState(false);
  const canPress =
    (status === 'draft' || status === 'failed') && !session.readOnly;
  const editable = props.editable === true && canPress;

  if (Array.isArray(emoji)) {
    // The served glyphs line up with the stored list only until it is edited
    const servedGlyphs =
      Array.isArray(props.glyph) && props.glyph.length === emoji.length
        ? props.glyph
        : [];
    const chosen = emoji.map((value, index) => ({
      value,
      glyph: glyphOf(value, servedGlyphs[index]),
    }));
    const limit = props.limit ?? 5;
    const toggle = (glyph: string) => {
      const value =
        chosen.find((entry) => entry.glyph === glyph)?.value ??
        (shortcode ? emojiToName[glyph] : glyph);
      if (!value) return;
      const picked = emoji.includes(value);
      if (!picked && emoji.length >= limit) return;
      setEmoji(
        picked ? emoji.filter((entry) => entry !== value) : [...emoji, value]
      );
      checks.afterChange();
      controls.commitNow();
    };
    return (
      <ReactionPills
        id={id}
        chosen={chosen}
        count={props.count}
        limit={limit}
        error={error}
        sizing={props.sizing}
        showAdd={
          props.editable === true &&
          (status === 'draft' || status === 'failed') &&
          !session.readOnly
        }
        interactive={editable}
        onToggle={toggle}
      />
    );
  }

  const glyph = glyphOf(
    emoji ?? '',
    typeof props.glyph === 'string' ? props.glyph : undefined
  );
  const pillClass = cn(
    PILL_CLASS,
    sizingClass(props.sizing),
    props.active ? ACTIVE_CLASS : 'border-kind-act bg-kind-act/[.08]',
    error && PILL_ERROR_CLASS
  );
  const count =
    props.count !== undefined ? (
      <span aria-hidden="true">{props.count}</span>
    ) : null;

  if (!editable) {
    return (
      <PillField id={id} error={error} className={sizingClass(props.sizing)}>
        <button
          type="button"
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? fieldErrorId(id) : undefined}
          aria-pressed={props.active === true}
          aria-label={props.pressLabel}
          disabled={!canPress}
          className={cn(pillClass, 'inline-flex items-center')}
          onClick={() => emit('press')}
        >
          <span
            aria-hidden="true"
            className="inline-flex items-center leading-none"
          >
            {glyph}
          </span>
          <span className="inline-flex items-center leading-none">{count}</span>
        </button>
      </PillField>
    );
  }

  return (
    <PillField id={id} error={error} className={sizingClass(props.sizing)}>
      <span className={pillClass}>
        <EmojiPickerPopover
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={(next) => {
            const value =
              props.emojiFormat === 'shortcode' ? emojiToName[next] : next;
            if (!value) return;
            setEmoji(value);
            checks.afterChange();
            controls.commitNow();
            setPickerOpen(false);
          }}
          trigger={
            <button
              type="button"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? fieldErrorId(id) : undefined}
              aria-label="Change emoji"
              className="inline-flex items-center leading-none"
            >
              {glyph}
            </button>
          }
        />
        <button
          type="button"
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? fieldErrorId(id) : undefined}
          aria-pressed={props.active === true}
          aria-label={props.pressLabel}
          disabled={!canPress}
          className="inline-flex items-center leading-none"
          onClick={() => emit('press')}
        >
          {count}
        </button>
      </span>
    </PillField>
  );
};
